import Head from 'next/head';
import { useMemo } from 'react';
import { Maybe, Page } from '@/graphql/__generated/sdk';
import { NextPageHeadSchemaWebpage } from '@shared-components/components/NextPage/NextPageHeadSchemaWebpage';
export function NextPageHeadSchema({
  title,
  description,
  breadcrumbs,
  breadcrumbTitle,
  originUrl,
  contentsCollection,
  path
}: Readonly<{
  breadcrumbs?: Maybe<Maybe<Page>[]>;
  breadcrumbTitle: Maybe<Page['breadcrumbTitle']>;
  originUrl: string;
  title: Maybe<Page['title']>;
  description: Maybe<Page['description']>;
  contentsCollection: Maybe<Page['contentsCollection']>;
  path?: Maybe<Page['path']>;
}>) {
  const graphedSchema = useMemo(() => {
    return contentsCollection?.items?.reduce<unknown[]>((acc, item: unknown) => {
      const typedItem = item as {
        combinedSchema?: boolean;
        schema?: unknown;
      };
      if (typedItem.combinedSchema && typedItem.schema) {
        acc.push(typedItem.schema);
      }
      return acc;
    }, []) || [];
  }, [contentsCollection]);
  if (graphedSchema.length) {
    return <GraphSchema key="webpage-schema-graph" title={title} description={description} breadcrumbs={breadcrumbs} breadcrumbTitle={breadcrumbTitle} originUrl={originUrl} graphedSchema={graphedSchema} path={path} />;
  }
  return <NextPageHeadSchemaWebpage key="webpage-schema-simple" title={title} description={description} breadcrumbs={breadcrumbs} breadcrumbTitle={breadcrumbTitle} originUrl={originUrl} path={path} data-sentry-element="NextPageHeadSchemaWebpage" data-sentry-component="NextPageHeadSchema" data-sentry-source-file="NextPageHeadSchema.tsx" />;
}
function GraphSchema({
  breadcrumbs,
  breadcrumbTitle,
  description,
  graphedSchema,
  originUrl,
  path,
  title
}: Readonly<{
  breadcrumbs?: Maybe<Maybe<Page>[]>;
  breadcrumbTitle?: Page['breadcrumbTitle'];
  description: Page['description'];
  originUrl: string;
  path?: Page['path'];
  title: Page['title'];
  graphedSchema: unknown[];
}>) {
  const replacementTokens: Record<string, string | undefined> = {
    title: title || undefined,
    description: description || undefined
  };

  // Inject a Service node for any WebPage.about @id that has no matching node in the graph
  const graphIds = new Set(graphedSchema.map((node: unknown) => (node as Record<string, unknown>)['@id']).filter(Boolean));
  const serviceNodes = graphedSchema.filter((node: unknown) => {
    const n = node as Record<string, unknown>;
    return n['@type'] === 'WebPage' && n['about'] !== null && typeof n['about'] === 'object';
  }).map((node: unknown) => {
    const about = (node as Record<string, unknown>)['about'] as Record<string, unknown>;
    return about?.['@id'] as string | undefined;
  }).filter((id): id is string => !!id && !graphIds.has(id)).map(id => ({
    '@type': 'Service',
    '@id': id,
    name: title || ''
  }));
  const currentPageUrl = `${originUrl}${path ?? ''}`;
  const toAbsoluteUrl = (url: string) => url.startsWith('http') ? url : `${originUrl}${url}`;
  const breadcrumbSchema = {
    '@type': 'BreadcrumbList',
    '@id': `${currentPageUrl}#breadcrumb`,
    itemListElement: [...(breadcrumbs || []), {
      path: currentPageUrl,
      breadcrumbTitle
    }].map((breadcrumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: breadcrumb?.breadcrumbTitle ?? breadcrumbTitle,
      item: breadcrumb?.path ? toAbsoluteUrl(breadcrumb.path) : currentPageUrl
    }))
  };
  const combinedSchemaString = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [...graphedSchema, ...serviceNodes, breadcrumbSchema]
  });
  const updateSchemaString = Object.entries(replacementTokens).reduce((acc, [key, value]) => {
    return acc.split(`[${key}]`).join(value ?? '');
  }, combinedSchemaString);
  return <Head data-sentry-element="Head" data-sentry-component="GraphSchema" data-sentry-source-file="NextPageHeadSchema.tsx">
			<script key="full-schema" type="application/ld+json" id="webpage-schema-graph" dangerouslySetInnerHTML={{
      __html: updateSchemaString
    }} />
		</Head>;
}